import "./MyProfile.css";
import CourseCard from "../ProfileComponents/CourseCard";
import EventCard from "../ProfileComponents/EventCard";
import useUserRole from "../hooks/useUserRole";
import { collection, doc, onSnapshot, query, setDoc, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGlobal } from "../context/GlobalContext";
import ReactConfetti from "react-confetti";
import AnimatedCard, { MotionItem } from "../Components/AnimatedCard";
import { motion, useReducedMotion } from "framer-motion";
import CourseFrame from "../Components/courseframe";
import planetBg from "../Images/planet-bg.png";
import courseThumb from "../Images/course-thumb.png";

/* ---------- helpers ---------- */
const DOTS = ["#3B6BFF", "#A855F7", "#EC4899", "#22D3EE", "#7C8DB5"];

// height of one notification row (px) - adjust if your EventCard is taller/shorter
const NOTIF_ROW_H = 86;
const NOTIF_VISIBLE = 3;
const SECTION_ROW_H = 28;   // height of each row in px
const SECTION_VISIBLE = 4;  // rows visible before scrolling

const toPercent = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
};

const normalizeLevels = (data) => {
  if (!data) return { levels: [], overall: null };
  let list = [];
  if (Array.isArray(data)) list = data;
  else if (Array.isArray(data.levels)) list = data.levels;
  else if (Array.isArray(data.sections)) list = data.sections;
  else
    list = Object.entries(data).map(([k, v]) =>
      typeof v === "object" ? { name: k, ...v } : { name: k, percent: v }
    );
  const levels = list.map((x) => ({
    id: x.id || x.sectionId,
    name: x.levelName || x.name || x.title,
    percent: toPercent(x.percent ?? x.percentage ?? x.progress ?? x.completion ?? x.completionPercentage),
  }));
  const nums = levels.map((l) => l.percent).filter((p) => p !== null);
  const overall =
    toPercent(data.overall ?? data.global ?? data.total) ??
    (nums.length ? Math.round(nums.reduce((a, b) => a + b, 0) / nums.length) : 0);
  return { levels, overall };
};

const cardBase =
  "rounded-[12px] border border-borderColor bg-[linear-gradient(135deg,rgba(10,20,80,0.75)_0%,rgba(6,12,50,0.85)_55%,rgba(5,9,38,0.92)_100%)]";

/* ---------- page ---------- */
export default function MyProfile({ setIsSidebarOpen, isSidebarOpen }) {
  const { sectionCompletionStatus } = useGlobal();
  const { user, pseudo } = useUserRole();
  const [sections, setSections] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [levelDataReady, setLevelDataReady] = useState(false);
  const reduceMotion = useReducedMotion();

  const completedCount = sections?.filter((s) => s.status === "completed").length || 0;
  const inProgressCount = sections?.filter((s) => s.status === "inprogres").length || 0;

  const { levels, overall } = useMemo(
    () => normalizeLevels(sectionCompletionStatus),
    [sectionCompletionStatus]
  );

  useEffect(() => {
    if (sectionCompletionStatus) {
      setLevelDataReady(true);
      return;
    }
    const fallback = setTimeout(() => setLevelDataReady(true), 1500);
    return () => clearTimeout(fallback);
  }, [sectionCompletionStatus]);

  useEffect(() => {
    setGlobalLoading(true);
    const unsub = onSnapshot(
      collection(db, "sections"),
      (snapshot) => {
        const arr = snapshot.docs
          .map((d) => ({ ...d.data(), id: d.id }))
          .sort((a, b) => a.order - b.order);
        setSections(arr);
        setGlobalLoading(false);
      },
      (error) => {
        console.error("Error getting sections data:", error);
        setGlobalLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    let unsub = () => { };
    if (user?.uid) {
      const q = query(collection(db, "notifications"), where("userId", "==", user.uid));
      unsub = onSnapshot(
        q,
        (snapshot) => {
          const arr = snapshot.docs
            .map((d) => ({ ...d.data(), id: d.id }))
            .sort((a, b) => b?.timestamp - a?.timestamp);
          setNotifications(arr);
        },
        (error) => console.error("Error getting notifications data:", error)
      );
    }
    return () => unsub();
  }, [user]);

  useEffect(() => {
    let unsub = () => { };
    let lastSigninWritten = false;
    if (user?.uid) {
      const userDocRef = doc(db, "users", user.uid);
      unsub = onSnapshot(userDocRef, (d) => {
        if (d.exists()) {
          const userData = d.data();
          if (!userData.lastSignin && !lastSigninWritten) {
            lastSigninWritten = true;
            setShowConfetti(true);
            setDoc(userDocRef, { lastSignin: new Date() }, { merge: true });
          }
        }
      });
    }
    return () => unsub();
  }, [user]);

  const percentFor = (section, i) => {
    const byId = levels.find((l) => l.id && l.id === section.id);
    return (byId ?? levels[i])?.percent ?? null;
  };

  const displayName = pseudo || user?.displayName || user?.firstName || "";
  const currentIdx = Math.max(0, sections.findIndex((s) => s.status === "inprogres"));
  const current = sections[currentIdx];
  const currentPercent = current ? percentFor(current, currentIdx) ?? 0 : 0;

  const header = (
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <button className="mt-1 lg:hidden" onClick={() => setIsSidebarOpen?.(!isSidebarOpen)} aria-label="Menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="text-left">
          <h1 className="text-[24px] font-bold leading-[30px] text-white">Bonjour {displayName}</h1>
          <p className="text-[12px] leading-[16px] text-[#A0AEC0]">Votre espace de progression.</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button className="flex h-[28px] w-[28px] items-center justify-center rounded-[8px] border border-borderColor bg-primary/80">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </button>
        <a
          href="#"
          className="hidden h-[28px] items-center gap-2 rounded-[8px] border border-borderColor bg-primaryBlue/60 px-3 text-[9px] font-semibold text-white sm:flex"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          Rejoindre la communauté
        </a>
      </div>
    </div>
  );

  const confetti = showConfetti && (
    <ReactConfetti width={window.innerWidth} height={window.innerHeight} recycle={false} tweenDuration={8000} run numberOfPieces={800} />
  );

  if (globalLoading || !levelDataReady) {
    return (
      <div className="flex flex-col gap-3 text-white">
        {confetti}
        {header}
      </div>
    );
  }

  const R = 38;
  const circ = 2 * Math.PI * R;

  return (
    <div className="flex flex-col gap-3 text-white">
      {confetti}
      {header}

      {/* ===== top row: resume | level | nouveautés ===== */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${cardBase} relative h-full min-h-[264px] overflow-hidden text-left`}
        >
          {/* planet background */}
          <div
            aria-hidden
            className="absolute inset-y-0 right-0 w-[48%] opacity-50 sm:w-[55%] sm:opacity-80 lg:w-[58%] lg:opacity-100"
            style={{
              backgroundImage: `url(${planetBg})`,
              backgroundSize: "cover",
              backgroundPosition: "right center",
              backgroundRepeat: "no-repeat",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 35%)",
              maskImage: "linear-gradient(to right, transparent 0%, #000 35%)",
            }}
          />

          <div className="relative z-10 px-[17px] pb-[32px] pt-[20px]">
            {/* label */}
            <div className="flex items-center gap-[8px] text-[11px] font-medium uppercase tracking-[0.02em] text-[#a9b9f0]">
              <span className="h-[12px] w-[12px] rounded-full bg-[#2f5bff] shadow-[0_0_10px_rgba(47,91,255,0.8)]" />
              Formation en cours
            </div>

            {/* heading */}
            <h2 className="mt-[6px] text-[22px] font-bold leading-[30px] text-white">
              Reprenez là où vous en étiez
            </h2>

            {/* current course */}
            <div className="mt-[20px] flex items-start gap-[14px]">
              <div className="h-[88px] w-[82px] shrink-0 overflow-hidden rounded-[8px] border border-borderColor bg-primary">
                <img
                  src={current?.image || current?.thumbnail || courseThumb}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0 pt-[3px]">
                <h3 className="text-[16px] font-bold leading-[22px] text-white">{current?.title}</h3>
                <p className="mt-[1px] text-[13px] leading-[18px] text-[#c7d0e6]">{current?.description}</p>
                <div className="mt-[14px] flex items-center gap-[8px]">
                  <div className="h-[12px] max-w-[177px] flex-1 overflow-hidden rounded-full bg-[#2a3470]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#1a3cff] to-[#3B6BFF]"
                      style={{ width: `${currentPercent}%` }}
                    />
                  </div>
                  <span className="text-[13px] text-[#c7d0e6]">{currentPercent}%</span>
                </div>
              </div>
            </div>

            {/* button */}
            <Link
              to="/resume"
              className="mt-[19px] inline-flex h-[44px] w-[219px] items-center justify-center gap-[16px] rounded-[8px] bg-gradient-to-r from-[#1a3cff] to-[#2f5bff] text-[13px] font-semibold text-white shadow-[0_0_18px_rgba(47,91,255,0.45)]"
            >
              <span className="flex h-[23px] w-[23px] items-center justify-center rounded-full bg-white">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#0a1a6e">
                  <path d="M7 4l13 8-13 8z" />
                </svg>
              </span>
              Reprendre la formation
            </Link>
          </div>
        </motion.div>

        {/* global level */}
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className={`${cardBase} h-full px-[17px] py-5 text-left`}
        >
          <h2 className="text-[15px] font-bold leading-[20px]">Mon niveau global</h2>
          <p className="mt-[3px] text-[10px] leading-[13px] text-[#A0AEC0]">
            Votre progression sur l'ensemble
            <br />
            du parcours.
          </p>
          <div className="relative mx-auto mt-[8px] h-[84px] w-[84px]">
            <svg viewBox="0 0 90 90" className="h-full w-full -rotate-90">
              <circle cx="45" cy="45" r={R} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
              <circle
                cx="45" cy="45" r={R} fill="none" stroke="#4268FF" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={circ * (1 - (overall || 0) / 100)}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[19px] font-bold">{overall || 0}%</span>
          </div>

          {/* shows exactly 4 rows, the rest scrolls */}
          <ul
            className="event-list mt-[9px] flex flex-none flex-col overflow-y-auto"
            style={{ maxHeight: SECTION_ROW_H * SECTION_VISIBLE }}
          >
            {sections.map((s, i) => {
              const p = percentFor(s, i);
              return (
                <li
                  key={s.id}
                  className="flex shrink-0 items-center justify-between border-b border-[#ffffff4d] text-[12px] font-bold leading-[13px] last:border-b-0"
                  style={{ height: SECTION_ROW_H }}
                >
                  <span className="flex items-center gap-2">
                    <span className="h-[7px] w-[7px] rounded-full" style={{ background: DOTS[i % 5] }} />
                    {s.title}
                  </span>
                  <span className="text-[#A0AEC0]">{p === null ? "—" : `${p}%`}</span>
                </li>
              );
            })}
          </ul>
        </motion.div>

        {/* nouveautés */}
        <motion.div
          initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className={`${cardBase} flex h-full flex-col px-[17px] pt-[10px] pb-[10px] text-left`}
        >
          <h2 className="text-[15px] font-bold leading-[20px]">Nouveautés</h2>
          {/* shows exactly 3 rows, the rest scrolls */}
          <div
            className="event-list mt-2 flex flex-none flex-col divide-y divide-white/10 overflow-y-auto"
            style={{ maxHeight: NOTIF_ROW_H * NOTIF_VISIBLE + (NOTIF_VISIBLE - 1) }}
          >
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                className="shrink-0 overflow-hidden"
                style={{ height: NOTIF_ROW_H }}
              >
                <EventCard notification={notification} index={index} />
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ===== Mes Formations ===== */}
      <AnimatedCard
        tilt={false}
        effects="light"
        delay={0.2}
        className="flex p-[12px_16px] flex-col items-start gap-[14px] rounded-[12px] bg-[#000000a6] border-[1px] border-[#ffffff4d]"
      >
        {/* header */}
        <MotionItem className="course-description w-full text-white">
          <div className="flex w-full flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="flex items-stretch gap-3">
              <motion.span
                aria-hidden
                className="w-[3px] origin-top rounded-full bg-gradient-to-b from-white to-[#A0AEC0]/10"
                initial={{ scaleY: reduceMotion ? 1 : 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
              <div>
                <h2
                  className="bg-clip-text text-left text-[15px] font-semibold leading-tight tracking-tight text-transparent md:text-[18px]"
                  style={{
                    backgroundImage: "linear-gradient(110deg, #fff 35%, #A0AEC0 50%, #fff 65%)",
                    backgroundSize: "250% 100%",
                    animation: reduceMotion ? "none" : "formationsShimmer 5s linear infinite",
                  }}
                >
                  Mes Formations
                </h2>
                <h4 className="mt-0.5 text-left text-[12px] text-[#a0aec0]">
                  Le Pouvoir de l'IA n'attend que toi..
                </h4>
              </div>
            </div>

            {/* stat chips */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: "formations", value: sections?.length || 0, pulse: false },
                { label: "en cours", value: inProgressCount, pulse: true },
                { label: "terminées", value: completedCount, pulse: false },
              ].map((chip, i) => (
                <motion.span
                  key={chip.label}
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.1, ease: "easeOut" }}
                  whileHover={reduceMotion ? undefined : { y: -2 }}
                  className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-2.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm transition-colors duration-300 hover:border-white/40 hover:bg-white/10"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    {!reduceMotion && chip.pulse && chip.value > 0 && (
                      <motion.span
                        aria-hidden
                        className="absolute inset-0 rounded-full bg-white"
                        animate={{ scale: [1, 2.6], opacity: [0.7, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                      />
                    )}
                    <span className="relative h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  <span className="font-semibold">{chip.value}</span>
                  <span className="text-[#A0AEC0]">{chip.label}</span>
                </motion.span>
              ))}
            </div>
          </div>

          {/* divider that draws itself, with a travelling light */}
          <div className="relative mt-3 h-px w-full overflow-hidden">
            <motion.span
              aria-hidden
              className="absolute inset-0 origin-left bg-gradient-to-r from-white/40 via-white/10 to-transparent"
              initial={{ scaleX: reduceMotion ? 1 : 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
            {!reduceMotion && (
              <motion.span
                aria-hidden
                className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-white to-transparent"
                initial={{ x: "-100%" }}
                animate={{ x: "1400%" }}
                transition={{ duration: 3, delay: 1.8, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
              />
            )}
          </div>
        </MotionItem>

        {/* course grid */}
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sections?.map((section) => (
            <Link
              to={"/resume"}
              key={section?.id}
              // kills any hover tilt/transform applied to the CourseFrame root
              className="block h-full [&>*]:!transform-none [&>*]:!transition-none"
            >
              <CourseFrame>
                <CourseCard
                  tagText={section.levelName}
                  CardColor=""
                  buttonColor={
                    section.status === "completed"
                      ? "bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1]"
                      : section.status === "inprogres"
                        ? "bg-custom-gradient"
                        : "bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1]"
                  }
                  CourseHeading={section.title}
                  CourseDescription={section.description}
                />
              </CourseFrame>
            </Link>
          ))}
        </div>
      </AnimatedCard>
    </div>
  );
}